import { Link, useParams } from 'react-router-dom';

import { useCanonicalTopic, useSubmitFeedback } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { RelevanceCurve } from 'src/components/editorial/RelevanceCurve';
import { Badge } from 'src/components/ui/Badge';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatDate } from 'src/lib/time';
import type { EditorialRelevanceWatch, EditorialTopicMention, EditorialTopicTrend } from 'src/types/editorial';

/**
 * Answers: what is this subject, how has it moved, and where was it said?
 *
 * The three questions are in that order on the screen because that is the order an editor asks
 * them. The mention list is last and links back into the broadcast it came from — a topic is an
 * index into the streams, not a replacement for them.
 */
export function TrendDetailWorkspace() {
  const { topicId = '' } = useParams<{ topicId: string }>();
  const query = useCanonicalTopic(topicId);

  if (query.isPending) {
    return (
      <div className="page-medium">
        <Skeleton variant="rows" count={5} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="page-medium">
        <LoadFailure title="No se pudo cargar el tema" error={query.error} onRetry={query.refetch} />
      </div>
    );
  }

  const { topic, trend, mentions, relevanceWatches } = query.data;

  return (
    <div className="page-medium">
      <header className="landmark">
        <Link className="topic-detail__back" to="/tendencias">
          ← Tendencias
        </Link>
        <h1 className="section-title">{topic.canonicalName || 'Tema sin título'}</h1>
        <p className="topic-detail__meta">
          {topic.mentionCount} mención{topic.mentionCount === 1 ? '' : 'es'}
          {topic.firstSeenAt && topic.lastSeenAt && ` · ${formatDate(topic.firstSeenAt)} — ${formatDate(topic.lastSeenAt)}`}
          {trend.resurfaced && trend.daysSincePrevious !== null && ` · volvió tras ${trend.daysSincePrevious} días`}
        </p>
      </header>

      {topic.aliases.length > 0 && (
        <section className="topic-detail__section">
          <h2 className="section-title">Cómo se ha nombrado</h2>
          {/* The aliases are the honest answer to "why did these stories end up together": they are
              the wordings the matcher actually saw, not a label anyone chose. */}
          <ul className="topic-tags topic-tags--small">
            {topic.aliases.slice(0, 12).map(alias => (
              <li key={alias} className="topic-tag">
                {alias}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="topic-detail__section">
        <h2 className="section-title">Cobertura en el tiempo</h2>
        <Sparkline trend={trend} />
      </section>

      {/* Below the coverage chart and above the mentions, because it is the same story at a finer
          resolution: that one buckets by day, this one is what happened inside the hours after a
          broadcast. A subject can be covered once and still have moved a great deal that afternoon. */}
      <RelevanceEvolution watches={relevanceWatches} />

      {trend.byChannel.length > 0 && <ChannelBreakdown byChannel={trend.byChannel} />}

      <section className="topic-detail__section">
        <h2 className="section-title">Menciones</h2>
        {mentions.length === 0 ? (
          <AbsenceState kind="empty" what="Este tema no tiene menciones registradas." />
        ) : (
          <ul className="mentions">
            {mentions.map(mention => (
              <MentionRow key={`${mention.topicId}-${mention.storyId}`} mention={mention} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Share of coverage per channel: the bar answers "who owns this subject" at a glance, the number
 *  keeps the count auditable. */
function ChannelBreakdown({ byChannel }: { byChannel: EditorialTopicTrend['byChannel'] }) {
  const total = byChannel.reduce((sum, channel) => sum + channel.mentions, 0);
  return (
    <section className="topic-detail__section">
      <h2 className="section-title">Quién lo cubrió</h2>
      <ul className="entity-list">
        {byChannel.map(channel => (
          <li key={channel.channelId || 'sin-canal'}>
            <span className="entity-list__name">{channel.channelName || 'Canal sin nombre'}</span>
            <span className="channel-bar" aria-hidden="true">
              <span className="channel-bar__fill" style={{ width: `${(channel.mentions / total) * 100}%` }} />
            </span>
            <span className="entity-list__mentions mono">{channel.mentions}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A bar per day the topic was mentioned, oldest left.
 *
 * Inline SVG rather than a charting dependency: this is a dozen rectangles, and the smallest chart
 * library in common use is larger than the entire rest of this screen. The timeline is sparse —
 * quiet days are absent, not zero — so the gaps between bars are themselves the resurfacing story.
 */
function Sparkline({ trend }: { trend: EditorialTopicTrend }) {
  if (trend.timeline.length === 0) {
    return <AbsenceState kind="empty" what="Sin menciones fechadas todavía." />;
  }

  // Narrowed once, here, rather than asserted at each use: `noUncheckedIndexedAccess` is right
  // that an index into an array proves nothing, and the guard above only proved it is non-empty.
  const timeline = trend.timeline;
  const first = timeline[0];
  const last = timeline[timeline.length - 1];
  if (!first || !last) return <AbsenceState kind="empty" what="Sin menciones fechadas todavía." />;

  const peak = Math.max(...timeline.map(point => point.mentions));
  const width = 100;
  // Bars sit at their date, not at their index: a three-week silence has to look longer than a
  // one-day one, or the resurfacing the gaps are supposed to show is invisible.
  const dayMs = 86_400_000;
  const dayOffset = (day: string) => (Date.parse(day) - Date.parse(first.day)) / dayMs;
  const spanDays = dayOffset(last.day);
  const slot = width / (spanDays + 1);

  return (
    <figure className="sparkline">
      <svg viewBox={`0 0 ${width} 24`} preserveAspectRatio="none" role="img" aria-label="Menciones por día">
        {timeline.map(point => {
          const height = (point.mentions / peak) * 22;
          return (
            <rect
              key={point.day}
              x={dayOffset(point.day) * slot}
              y={24 - height}
              width={Math.max(slot - 0.6, 0.4)}
              height={height}
              className="sparkline__bar"
              data-peak={point.mentions === peak ? '' : undefined}
            >
              <title>{`${point.day}: ${point.mentions}`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="sparkline__caption">
        <span>{first.day}</span>
        {trend.peak && (
          <span className="mono">
            pico {trend.peak.mentions} el {trend.peak.day}
          </span>
        )}
        <span>{last.day}</span>
      </figcaption>
    </figure>
  );
}

/**
 * Every relevance curve this topic has, newest first.
 *
 * Newest first and not merged into one line: each curve belongs to one story of one broadcast, and
 * splicing three days of them into a single series would invent a trajectory nobody measured.
 */
function RelevanceEvolution({ watches }: { watches: Array<EditorialRelevanceWatch> }) {
  if (watches.length === 0) return null;

  const recent = [...watches].reverse().slice(0, 5);

  return (
    <section className="topic-detail__section">
      <h2 className="section-title">Evolución de relevancia</h2>
      <p className="topic-detail__note">
        Cada historia de este tema se mide en X al salir de la transmisión y se vuelve a leer durante las cinco horas
        siguientes, mientras la caja esté capturando. La línea de 65 es donde el pipeline actúa; la de 10, donde empieza
        el seguimiento. Es atención, no veracidad.
      </p>
      <ul className="watch-list">
        {recent.map(watch => (
          <li key={watch.watchId}>
            <p className="watch-list__headline">{watch.headline || watch.storyId}</p>
            <RelevanceCurve watch={watch} />
          </li>
        ))}
      </ul>
      {watches.length > recent.length && (
        // Deliberately not "de {watches.length}": the server sends the recent page, not the
        // topic's whole history, so a total quoted from it would be a number about the page.
        <p className="topic-detail__note">Se muestran las {recent.length} curvas más recientes.</p>
      )}
    </section>
  );
}

/** How the link was made, in the words the schema uses. Kept visible because a topic an editor
 *  disagrees with is only disputable if the reason it was joined is on screen. */
const MATCH_LABEL: Record<string, string> = {
  exact: 'mismo asunto (juez)',
  semantic: 'similitud',
  related: 'relacionado',
  subtopic: 'subtema',
  new: 'abrió el tema',
};

/**
 * The input the matcher's tier-4 learning was missing: a rejected join counts toward loosening
 * nothing and tightening this category's merge threshold on the next backfill. The verdict targets
 * the (topic, story) pair, which is how the pipeline reads the category back off it.
 */
function MentionFeedback({ mention }: { mention: EditorialTopicMention }) {
  const feedback = useSubmitFeedback();

  if (feedback.isSuccess) return <span className="mention__feedback-done">gracias</span>;

  return (
    <span className="mention__feedback">
      <button
        type="button"
        className="mention__feedback-button"
        disabled={feedback.isPending}
        title="La unión es correcta: esta historia sí es este tema"
        onClick={() =>
          feedback.mutate({
            targetType: 'topic_mention',
            targetId: `${mention.topicId}::${mention.storyId}`,
            verdict: 'approved',
            reason: `match_type=${mention.matchType}`,
          })
        }
      >
        ✓
      </button>
      <button
        type="button"
        className="mention__feedback-button"
        disabled={feedback.isPending}
        title="Unión incorrecta: esta historia no pertenece a este tema"
        onClick={() =>
          feedback.mutate({
            targetType: 'topic_mention',
            targetId: `${mention.topicId}::${mention.storyId}`,
            verdict: 'rejected',
            reason: `match_type=${mention.matchType}`,
          })
        }
      >
        ✗
      </button>
    </span>
  );
}

function MentionRow({ mention }: { mention: EditorialTopicMention }) {
  return (
    <li className="mention">
      <div className="mention__what">
        <p className="mention__headline">{mention.headline || 'Historia sin título'}</p>
        <p className="mention__where">
          {mention.channelName && <span>{mention.channelName} · </span>}
          {mention.mentionedAt && <span>{formatDate(mention.mentionedAt)}</span>}
        </p>
      </div>

      <div className="mention__how">
        <Badge tone={mention.matchType === 'new' ? 'unresolved' : 'supported'}>
          {MATCH_LABEL[mention.matchType] ?? mention.matchType}
        </Badge>
        {mention.matchConfidence > 0 && <span className="mono mention__score">{mention.matchConfidence.toFixed(2)}</span>}
        {/* Only joins are disputable — a mention that opened its own topic merged nothing, and a
            "rejected" on it would teach the matcher the wrong lesson. */}
        {mention.matchType !== 'new' && <MentionFeedback mention={mention} />}
      </div>

      {/* A topic is an index into the broadcasts, so every mention leads back to one. */}
      {mention.streamId && (
        <Link className="mention__link" to={`/stream/${encodeURIComponent(mention.streamId)}/stories`}>
          Ver transmisión
        </Link>
      )}
    </li>
  );
}
